import type { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { View } from 'react-native'

import { Button } from '@/src/elements/button/default'
import { Icon } from '@/src/elements/media/Icon'
import { Typography } from '@/src/elements/text/Typography'
import { useCommunityJoinSteps } from '@/src/features/community/ui/hooks/useCommunityJoinSteps'
import { FraternityOnboardingWrapper } from '@/src/features/fraternity/ui/components/FraternityOnboardingWrapper'
import { ICON } from '@/src/general/enums/icons'
import { cn } from '@/src/general/utils/cn'

type TFraternityPresentationScreenProps = TIdSlugParam & TSearchParamsParam

const presentationItems = [
  {
    icon: ICON.MESSAGE_HEART,
    messageKey: 'fraternity.presentation.item.encourage' as const,
    iconBgClassName: 'bg-accents-lime-subtle',
    iconClassName: 'text-accents-lime-default',
  },
  {
    icon: ICON.MESSAGE_CHAT,
    messageKey: 'fraternity.presentation.item.share' as const,
    iconBgClassName: 'bg-accents-teal-subtle',
    iconClassName: 'text-accents-teal-default',
  },
  {
    icon: ICON.STARS,
    messageKey: 'fraternity.presentation.item.pray' as const,
    iconBgClassName: 'bg-accents-pink-subtle',
    iconClassName: 'text-accents-pink-default',
  },
]

export const FraternityPresentationScreen = ({
  idSlug,
  searchParams,
}: TFraternityPresentationScreenProps): ReactNode => {
  const joinSteps = useCommunityJoinSteps({
    idSlug,
    searchParams,
    stepName: 'presentation',
  })
  const { goToNextStep } = joinSteps

  return (
    <FraternityOnboardingWrapper
      joinSteps={joinSteps}
      title={<FormattedMessage id="fraternity.presentation.title" />}
      subtitle={<FormattedMessage id="fraternity.presentation.subtitle" />}
      footer={
        <Button colors="primary" variant="solid" fullWidth onPress={goToNextStep}>
          <FormattedMessage id="word.continue" />
        </Button>
      }
    >
      <View className="gap-6">
        {presentationItems.map(({ icon, messageKey, iconBgClassName, iconClassName }) => (
          <View key={messageKey} className="flex-row items-center gap-4">
            <View className={cn('rounded-lg p-2', iconBgClassName)}>
              <Icon name={icon} size="md" className={iconClassName} />
            </View>
            <Typography className="flex-1">
              <FormattedMessage id={messageKey} />
            </Typography>
          </View>
        ))}
      </View>
    </FraternityOnboardingWrapper>
  )
}
